import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDialog } from '../../utils/useDialog';
import { ModalCloseButton } from '../ModalCloseButton/ModalCloseButton';
import './ModalShell.scss';

export interface ModalShellProps {
  /** Called when the visitor closes the modal: close button, overlay click or Escape */
  onClose: () => void;
  /** Id of the visible heading that names the dialog; takes precedence over `ariaLabel` */
  labelledBy?: string;
  /** Accessible name used when there is no `labelledBy`; required so the dialog is never unnamed */
  ariaLabel: string;
  /** Accessible name of the close button */
  closeLabel?: string;
  /** Extra class on the panel, for the content block's own layout */
  className?: string;
  /** Element focused when the modal closes; defaults to whatever had focus when it opened */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /** Modal content */
  children: React.ReactNode;
}

/**
 * Shared modal frame: dimmed overlay, a VideoModal-style close row above the content, and an
 * off-white rounded panel that scrolls inside itself when its content is taller than the viewport.
 * Escape, the Tab trap, focus restore and the page scroll lock come from `useDialog`.
 * Render it only while open; it portals to `document.body` so no block's overflow clips it.
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  onClose,
  labelledBy,
  ariaLabel,
  closeLabel = 'Close',
  className,
  returnFocusRef,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useDialog({
    containerRef: rootRef,
    onEscape: onClose,
    initialFocusRef: closeRef,
    restoreFocus: true,
    returnFocusRef,
    lockScroll: true,
  });

  return createPortal(
    <div ref={rootRef} className="poly-modal-shell">
      <div className="poly-modal-shell__overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="poly-modal-shell__container"
        role="dialog"
        aria-modal="true"
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': ariaLabel })}
      >
        <div className="poly-modal-shell__topbar">
          <ModalCloseButton
            buttonRef={closeRef}
            className="poly-modal-shell__close"
            surface="dark"
            onClick={onClose}
            ariaLabel={closeLabel}
          />
        </div>
        <div className={`poly-modal-shell__panel${className ? ` ${className}` : ''}`}>
          {/* A Tab stop so keyboard users can scroll long content that has no links or buttons. */}
          <div className="poly-modal-shell__body" tabIndex={0}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
